import {
  type BracketMatch,
  type BracketSide,
  type BracketState,
  BYE,
  drawBracket,
  TOURNAMENT_REGIONS,
  type TournamentAnswer,
  type TournamentRegion,
  type TournamentView,
} from '@gardener/protocol';
import { createResource, createSignal, For, Show } from 'solid-js';
import { call } from './api';
import {
  EmptyState,
  Icon,
  LoadingLine,
  SETTINGS_INPUT,
  SettingsCard,
  Switch,
  Toolbar,
  ToolbarGroup,
  toast,
  Well,
} from './ui';

/** A `datetime-local` value (this browser's time zone) of an ISO time, and back. */
const local = (iso: string) => {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};
/** The players as typed: one a line, best seed first; `?` a seat nobody has yet (TBD). */
const seatsOf = (text: string) =>
  text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => (l === '?' ? null : l));
const sideName = (s: BracketSide) => (s === BYE ? 'bye' : (s ?? 'TBD'));
/** A match's state as logdash's uptime rows show a monitor's: a dot, and a word in its tone. */
const STATE: Record<BracketState, { dot: string; text: string; label: string }> = {
  live: { dot: 'bg-error', text: 'text-error-fg', label: 'Live' },
  done: { dot: 'bg-success', text: 'text-fg-muted', label: 'Done' },
  ready: { dot: 'bg-info', text: 'text-fg-tertiary', label: 'Ready' },
  waiting: { dot: 'bg-idle', text: 'text-fg-muted', label: 'Waiting' },
  bye: { dot: 'bg-idle', text: 'text-fg-muted', label: 'Bye' },
};

/**
 * The preseason tournament (ADR-0137, garden-wars.io/tournament), a region at a time: when it starts and its players
 * (`PATCH /api/admin/tournament/:region`), then each match's result as it is played (`PUT` / `DELETE …/results/:id`).
 * The page polls, so a result shows there within half a minute.
 */
export function Tournament() {
  const [region, setRegion] = createSignal<TournamentRegion>('eu');
  const [view, { mutate, refetch }] = createResource(
    region,
    async (r) => (await call<TournamentAnswer>('GET', `/api/tournament/${r}`)).tournament,
  );
  /** An admin call answered with the tournament after it; true once it went through. */
  const admin = async (method: string, path: string, body?: unknown, said = 'Saved'): Promise<boolean> => {
    try {
      mutate((await call<TournamentAnswer>(method, `/api/admin/tournament/${region()}${path}`, body)).tournament);
      toast(said);
      return true;
    } catch (err) {
      toast((err as Error).message, 'error');
      return false;
    }
  };

  return (
    <div class="@container flex w-full flex-col">
      <Toolbar>
        <ToolbarGroup
          label="Region"
          value={region()}
          options={TOURNAMENT_REGIONS.map((r) => [r, r.toUpperCase()] as const)}
          onChange={setRegion}
        />
      </Toolbar>
      <Show
        // An errored resource throws when read: its error first.
        when={!view.error && view()}
        keyed
        fallback={
          <div class="p-2">
            <Well label="Tournament">
              <Show
                when={view.error}
                fallback={
                  <div class="px-3 py-2.5">
                    <LoadingLine label="Loading the tournament" />
                  </div>
                }
              >
                {(err) => (
                  <EmptyState centered title="Could not load the tournament" description={(err() as Error).message}>
                    <button type="button" class="ld-button" data-size="sm" onClick={() => void refetch()}>
                      Try again
                    </button>
                  </EmptyState>
                )}
              </Show>
            </Well>
          </div>
        }
      >
        {(t) => (
          <div class="grid items-start gap-2 p-2 @4xl:grid-cols-[minmax(0,1fr)_26rem]">
            <Results t={t} admin={admin} />
            <Setup t={t} save={(body) => admin('PATCH', '', body, 'The tournament is set up')} />
          </div>
        )}
      </Show>
    </div>
  );
}

/** When it starts and who plays, seeded: logdash's settings card, a field an item. */
function Setup(props: {
  t: TournamentView;
  save: (body: { startsAt: string; players: (string | null)[] }) => Promise<boolean>;
}) {
  const [start, setStart] = createSignal(local(props.t.startsAt));
  const [text, setText] = createSignal(props.t.players.map((p) => p ?? '?').join('\n'));
  const summary = () => {
    const seats = seatsOf(text());
    let size = 4;
    while (size < seats.length) size *= 2;
    const tbd = seats.filter((s) => s === null).length;
    const byes = size - seats.length;
    return `${size} seats: ${seats.length - tbd} players, ${tbd} TBD, ${byes} byes${byes ? ' (the top seeds get them)' : ''}`;
  };
  return (
    <form
      class="min-w-0 @4xl:sticky @4xl:top-2"
      aria-label="Set up"
      onSubmit={(e) => {
        e.preventDefault();
        void props.save({ startsAt: new Date(start()).toISOString(), players: seatsOf(text()) });
      }}
    >
      <SettingsCard title="Set up" description="When it starts and who plays.">
        <label class="flex flex-col gap-2 px-3 py-2.5">
          <span class="text-fg-muted text-xs">Starts (your time zone)</span>
          <input
            type="datetime-local"
            class={`${SETTINGS_INPUT} w-full`}
            data-size="sm"
            value={start()}
            onInput={(e) => setStart(e.currentTarget.value)}
          />
          <span class="text-fg-muted text-xs">{new Date(start()).toUTCString()}</span>
        </label>
        <label class="flex flex-col gap-2 px-3 py-2.5">
          <span class="text-fg-muted text-xs">Players, best seed first</span>
          <textarea
            rows={12}
            class={`${SETTINGS_INPUT} block h-auto w-full resize-y py-1.5 leading-6 whitespace-pre-wrap`}
            data-size="sm"
            value={text()}
            onInput={(e) => setText(e.currentTarget.value)}
          />
          <span class="text-fg-muted text-xs">One a line; ? for a seat nobody has yet.</span>
          <span class="text-fg-tertiary text-xs tabular-nums">{summary()}</span>
        </label>
        <div class="flex justify-end px-3 pt-2.5 pb-1">
          <button type="submit" class="ld-button" data-variant="primary" data-size="sm">
            Save
          </button>
        </div>
      </SettingsCard>
    </form>
  );
}

/** Every match, round by round: a settings card whose items are the rounds, each a list of rows between hairlines. */
function Results(props: {
  t: TournamentView;
  admin: (method: string, path: string, body?: unknown, said?: string) => Promise<boolean>;
}) {
  const bracket = () => drawBracket(props.t);
  const rounds = () => {
    const b = bracket();
    return [
      ...b.winners.map((r, i) => ({ name: `Winners round ${i + 1}`, matches: r })),
      ...b.losers.map((r, i) => ({ name: `Losers round ${i + 1}`, matches: r })),
      { name: 'Grand final', matches: b.final },
    ];
  };
  return (
    <SettingsCard
      title="Results"
      description="Games won by the top and the bottom player; live: being played now. A match is scored once both are known."
      class="@container"
      actions={
        <Show when={bracket().champion}>
          {(c) => (
            <span class="ld-badge" data-variant="success" data-size="sm">
              <Icon name="trophy" class="-ml-0.5 size-3" />
              {c()}
            </span>
          )}
        </Show>
      }
    >
      <For each={rounds()}>
        {(round) => (
          <div class="flex min-w-0 flex-col pt-2">
            <h3 class="text-fg-muted flex h-6 items-center px-3 text-xs">{round.name}</h3>
            <ul class="edge-between flex flex-col">
              <For each={round.matches}>{(m) => <MatchRow m={m} admin={props.admin} />}</For>
            </ul>
          </div>
        )}
      </For>
    </SettingsCard>
  );
}

/**
 * A match as logdash's `MonitorRow` draws a monitor: its dot, its id and players, then the score to send (once both
 * players are known) and its state on the right. A narrow card puts the score on a line of its own.
 */
function MatchRow(props: {
  m: BracketMatch;
  admin: (method: string, path: string, body?: unknown, said?: string) => Promise<boolean>;
}) {
  const m = props.m;
  const [top, setTop] = createSignal(m.score?.[0] ?? 0);
  const [bottom, setBottom] = createSignal(m.score?.[1] ?? 0);
  const [live, setLive] = createSignal(m.state === 'live');
  const path = `/results/${m.id}`;
  const playable = m.state === 'ready' || m.state === 'live' || m.state === 'done';
  const state = STATE[m.state];
  /** A side's name: the winner's raised, an unknown seat or a bye dimmed. */
  const side = (i: 0 | 1) => (
    <span
      class="truncate"
      classList={{
        'font-medium': m.won === i,
        'text-fg-muted': m.sides[i] === null || m.sides[i] === BYE,
        'text-fg-tertiary': m.state === 'done' && m.won !== i,
      }}
    >
      {sideName(m.sides[i])}
    </span>
  );
  const score = (value: number, set: (n: number) => void, label: string) => (
    <input
      type="number"
      min="0"
      max="99"
      aria-label={label}
      class={`${SETTINGS_INPUT} font-figure w-11 [appearance:textfield] px-0 text-center tabular-nums [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`}
      data-size="sm"
      value={value}
      onInput={(e) => set(e.currentTarget.valueAsNumber)}
    />
  );
  return (
    <li class="grid min-h-12 grid-cols-[0.375rem_2.5rem_minmax(0,1fr)_3.5rem] items-center gap-x-3 gap-y-2 px-3 py-1.5 text-[13px] @min-[40rem]:grid-cols-[0.375rem_2.5rem_minmax(0,1fr)_auto_3.5rem]">
      <span class={`size-1.5 rounded-full ${state.dot}`} />
      <span class="text-fg-muted truncate font-mono text-xs">{m.id}</span>
      <span class="flex min-w-0 flex-col leading-[1.125rem]">
        {side(0)}
        {side(1)}
      </span>
      <Show when={playable}>
        <div class="col-span-3 col-start-2 row-start-2 flex items-center gap-3 @min-[40rem]:col-span-1 @min-[40rem]:col-start-4 @min-[40rem]:row-start-1">
          <span class="flex items-center gap-1.5">
            {score(top(), setTop, `${m.id} top`)}
            <span class="text-fg-muted">:</span>
            {score(bottom(), setBottom, `${m.id} bottom`)}
          </span>
          <span class="flex items-center gap-2">
            <Switch size="xs" id={`${m.id}-live`} label={`${m.id} live`} checked={live()} onChange={setLive} />
            <label for={`${m.id}-live`} class="text-fg-tertiary cursor-pointer select-none">
              Live
            </label>
          </span>
          <span class="ml-auto flex items-center gap-1">
            <button
              type="button"
              class="ld-button"
              data-size="sm"
              onClick={() => props.admin('PUT', path, { score: [top(), bottom()], live: live() }, `${m.id} scored`)}
            >
              Save
            </button>
            <button
              type="button"
              class="ld-button"
              classList={{ invisible: !m.score }}
              data-variant="ghost"
              data-size="sm"
              onClick={() => props.admin('DELETE', path, undefined, `${m.id} cleared`)}
            >
              Clear
            </button>
          </span>
        </div>
      </Show>
      <span class={`col-start-4 row-start-1 text-right @min-[40rem]:col-start-5 ${state.text}`}>{state.label}</span>
    </li>
  );
}
